/** Sign in, with the MFA step the backend may demand. */

import { useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, ErrorState } from '@/components/ui';
import { useSessionContext } from '@/features/auth/session';
import { ApiError } from '@/lib/api';

/** The login view. */
export function LoginPage(): ReactElement {
  const { signIn, submitMfa } = useSessionContext();
  const navigate = useNavigate();
  const [tenantSlug, setTenantSlug] = useState(
    () => window.localStorage.getItem('prp.tenant') ?? '',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (needsMfa) {
        await submitMfa(code);
      } else {
        window.localStorage.setItem('prp.tenant', tenantSlug);
        await signIn(tenantSlug, email, password);
      }
      navigate('/', { replace: true });
    } catch (caught) {
      // `MFA_REQUIRED` is the backend saying "password accepted, now prove the second
      // factor". It is a step, not a failure, and showing it as an error would teach an
      // analyst that the normal path produces red text.
      //
      // Matched on the problem-document code rather than the status: this tested for
      // HTTP 428 while the API answers 401, so the code field never appeared and
      // enrolling in MFA locked you out of the browser entirely.
      if (caught instanceof ApiError && caught.code === 'MFA_REQUIRED') {
        setNeedsMfa(true);
      } else {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto mt-24 max-w-sm">
      <Card title="Sign in">
        <form onSubmit={(event) => void onSubmit(event)} className="space-y-3">
          {!needsMfa ? (
            <>
              <label className="block text-sm">
                <span className="text-muted">Workspace</span>
                <input
                  autoComplete="organization"
                  required
                  value={tenantSlug}
                  onChange={(event) => setTenantSlug(event.target.value)}
                  placeholder="your-workspace"
                  className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-muted">Email</span>
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-muted">Password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
                />
              </label>
            </>
          ) : (
            <label className="block text-sm">
              <span className="text-muted">Authenticator code</span>
              <input
                // Not `inputMode="numeric"`: a recovery code is accepted here too, and a
                // numeric keypad on a phone cannot type one. The field takes either.
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
                className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 font-mono text-sm"
              />
              <span className="mt-1 block text-xs text-muted">
                Six digits from your authenticator, or one of your recovery codes.
              </span>
            </label>
          )}
          {error != null && <ErrorState error={error} />}
          <Button type="submit" tone="primary" disabled={busy} className="w-full justify-center">
            {busy ? 'Working…' : needsMfa ? 'Verify' : 'Sign in'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
